// src/components/Navbar.jsx
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FaBars, FaTimes } from "react-icons/fa";
import API from "../api/api";
import { resolveAssetUrl } from "../api/config";

const FALLBACK_MENU = [
  { name: "Home", path: "/" },
  { name: "Events", path: "/events" },
  { name: "Gallery", path: "/gallery" },
  { name: "Players", path: "/players" },
  { name: "News", path: "/news" },
  { name: "Updates & Results", path: "/updates-results" },
  { name: "Messages", path: "/messages" },
  { name: "Donate", path: "/donate" },
  { name: "Volunteer", path: "/volunteer" },
  { name: "Partner", path: "/partner" },
  { name: "About Us", path: "/about-us" },
];

const HEADER_SECTION_KEYS = [
  "site_header_president",
  "site_header_leader",
  "site_header_profile",
];

const FALLBACK_HEADER_PROFILE = {
  name: "Sri Gonuguntla Koteswara Rao",
  lines: ["President - Para Sports Association of Andhra Pradesh"],
  imageSrc: "/images/president.jpg",
};

const HEADER_SHELL_CLASS =
  "relative lg:sticky lg:top-0 z-50 border-b border-emerald-400 bg-gradient-to-r from-emerald-300/95 via-teal-200/90 to-cyan-200/90 lg:backdrop-blur lg:supports-[backdrop-filter]:bg-emerald-200/95 shadow-[0_10px_24px_rgba(5,150,105,0.22)]";

const HEADER_SURFACE_CLASS =
  "bg-gradient-to-r from-emerald-300/95 via-teal-200/90 to-cyan-200/90 lg:backdrop-blur lg:supports-[backdrop-filter]:bg-emerald-200/95";

const ADMIN_MENU = [
  { name: "Dashboard", path: "/admin", roles: ["admin", "secretary", "district"] },
  { name: "Players", path: "/admin/manage-players", roles: ["admin", "secretary", "district"] },
  { name: "Events", path: "/admin/manage-events", roles: ["admin", "secretary", "district"] },
  { name: "Registrations", path: "/admin/event-registrations", roles: ["admin", "secretary", "district"] },
  { name: "Classification", path: "/admin/classification-allotment", roles: ["admin", "secretary", "district"] },
  { name: "Game Day", path: "/admin/game-day", roles: ["admin", "secretary", "district"] },
  { name: "Results", path: "/admin/results", roles: ["admin", "secretary", "district"] },
  { name: "Certificates", path: "/admin/certificates", roles: ["admin", "secretary", "district"] },
  { name: "Gallery", path: "/admin/gallery", roles: ["admin", "secretary"] },
  { name: "Occasions", path: "/admin/occasion-posters", roles: ["admin", "secretary"] },
  { name: "Reports", path: "/admin/reports", roles: ["admin", "secretary"] },
  { name: "Mail", path: "/admin/mail-center", roles: ["admin", "secretary"] },
];

const NavLink = ({ children, active = false, ...props }) => (
  <button
    className={`px-2.5 py-1.5 rounded-full text-[11px] xl:text-xs 2xl:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
      active
        ? "bg-emerald-200 text-emerald-900 shadow-sm"
        : "text-slate-700 hover:text-emerald-900 hover:bg-emerald-100"
    }`}
    {...props}
  >
    {children}
  </button>
);

const splitTextLines = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location?.pathname || "/";
  const { user, token, logout } = useContext(AuthContext);
  const [menuItems, setMenuItems] = useState(FALLBACK_MENU);
  const [headerSections, setHeaderSections] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch menu items
  useEffect(() => {
    let mounted = true;
    const fetchMenu = async () => {
      try {
        const endpoint = token ? "/menu" : "/menu/public";
        const res = await API.get(endpoint);
        if (mounted) {
          const normalizedMenu = Array.isArray(res?.menu)
            ? res.menu
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];
          setMenuItems(
            normalizedMenu.length > 0 ? normalizedMenu : FALLBACK_MENU
          );
        }
      } catch (err) {
        console.error("Menu load failed", err);
        if (mounted) setMenuItems(FALLBACK_MENU);
      }
    };
    fetchMenu();
    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    let mounted = true;
    const fetchHeaderSections = async () => {
      try {
        const rows = await API.fetchAboutSections("site_header");
        if (!mounted) return;
        setHeaderSections(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Header content load failed", err);
        if (mounted) setHeaderSections([]);
      }
    };
    fetchHeaderSections();
    return () => {
      mounted = false;
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Body scroll lock for mobile menu
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    if (mobileMenu) {
      body.style.overflow = "hidden";
      html.style.overflow = "hidden";
    } else {
      body.style.overflow = "";
      html.style.overflow = "";
    }
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [mobileMenu]);

  useEffect(() => {
    setMobileMenu(false);
    setShowDropdown(false);
  }, [currentPath]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) {
        setMobileMenu(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const displayName = user?.name || "User";
  const role = (user?.role || "player").toString().toLowerCase();
  const isLoggedIn = Boolean(token);
  const isAdminArea = /^\/admin(\/|$)/.test(currentPath);
  const adminMenuItems = useMemo(
    () => ADMIN_MENU.filter((item) => item.roles.includes(role)),
    [role]
  );
  const parsedMenuItems = Array.isArray(menuItems)
    ? menuItems.filter(
        (item) =>
          item &&
          typeof item.name === "string" &&
          item.name.trim() !== "" &&
          typeof item.path === "string" &&
          item.path.trim() !== ""
      )
    : [];
  const safeMenuItems =
    parsedMenuItems.length > 0 ? parsedMenuItems : FALLBACK_MENU;
  const navItems = isAdminArea ? adminMenuItems : safeMenuItems;
  const headerSection = useMemo(() => {
    const activeSections = (Array.isArray(headerSections) ? headerSections : []).filter(
      (section) => section?.is_active !== false
    );
    return (
      HEADER_SECTION_KEYS
        .map((key) =>
          activeSections.find(
            (section) => String(section?.section_key || "").trim().toLowerCase() === key
          )
        )
        .find(Boolean) ||
      activeSections[0] ||
      null
    );
  }, [headerSections]);
  const headerName = String(headerSection?.heading || "").trim() || FALLBACK_HEADER_PROFILE.name;
  const headerLines = [
    String(headerSection?.subheading || "").trim(),
    ...splitTextLines(headerSection?.body),
  ]
    .filter(Boolean)
    .filter(
      (line) =>
        !/affiliated to paralympic committee of india/i.test(String(line || ""))
    );
  const resolvedHeaderLines =
    headerLines.length > 0 ? headerLines : FALLBACK_HEADER_PROFILE.lines;
  const isItemActive = (path) => {
    const cleanPath = String(path || "").split("?")[0];
    if (!cleanPath) return false;
    return (
      currentPath === cleanPath ||
      (cleanPath !== "/" && currentPath.startsWith(`${cleanPath}/`))
    );
  };
  const photoUrl = resolveAssetUrl(user?.photo, {
    folder: "players",
    entityId: user?.id,
    defaultPath: "/images/placeholder-user.png",
  });
  const headerPhotoUrl = resolveAssetUrl(headerSection?.image_path || "", {
    folder: "page-sections",
    defaultPath: FALLBACK_HEADER_PROFILE.imageSrc,
  });

  if (isAdminArea) {
    return (
      <header className={HEADER_SHELL_CLASS}>
        <div className="w-full flex justify-between items-center gap-2 sm:gap-3 px-[clamp(0.5rem,2vw,2rem)] py-2.5 sm:py-3">
          <div
            className="min-w-0 flex items-center gap-2 sm:gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            {!isLoggedIn ? (
              <div className="h-[clamp(2.6rem,5vw,4.2rem)] w-auto rounded-none bg-transparent border-0 shadow-none flex items-center justify-center shrink-0">
                <img
                  src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                  alt="Logo"
                  className="h-full w-auto object-contain"
                />
              </div>
            ) : null}
            {!isLoggedIn ? (
              <p className="text-[clamp(0.62rem,0.95vw,0.95rem)] font-semibold text-slate-900 leading-tight max-w-[clamp(9rem,20vw,21rem)]">
                Para Sports Association of Andhra Pradesh
              </p>
            ) : (
              <p className="text-[clamp(0.62rem,0.95vw,0.95rem)] font-semibold text-slate-900 leading-tight max-w-[clamp(9rem,20vw,21rem)]">
                Menu
              </p>
            )}
          </div>

          <nav className="hidden xl:flex items-center gap-1.5 bg-white/95 border border-emerald-200 rounded-full px-2.5 py-1.5 shadow-sm">
            {navItems.map((item, idx) => (
              <NavLink
                key={item.path || `${item.name || "menu"}-${idx}`}
                active={isItemActive(item.path)}
                onClick={() => navigate(item.path)}
              >
                {item.name}
              </NavLink>
            ))}

            {token ? (
              <div className="relative" ref={dropdownRef}>
                <div
                  onClick={() => setShowDropdown((s) => !s)}
                  className="flex items-center gap-3 cursor-pointer rounded-full pl-2 pr-3 py-1.5 hover:bg-emerald-100 transition-colors"
                >
                  <img
                    src={photoUrl}
                    alt="User"
                    className="w-10 h-10 rounded-full object-cover border-2 border-emerald-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/placeholder-user1.png";
                    }}
                  />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                    <div className="text-xs text-gray-500 capitalize">{role}</div>
                  </div>
                </div>

                {showDropdown && (
                  <div className="absolute right-0 mt-3 bg-white text-black rounded-2xl shadow-xl w-56 p-2 border border-emerald-200">
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        if (role === "player") navigate("/player-profile");
                        else if (["admin", "secretary", "district"].includes(role)) navigate("/admin/profile");
                        else navigate("/player-profile");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/change-password");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-rose-50 text-rose-600 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/login")}
                  className="font-semibold text-emerald-900 border border-emerald-300 bg-emerald-100 rounded-full px-4 py-2 hover:bg-emerald-200 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full px-5 py-2 hover:from-emerald-700 hover:to-teal-700 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  Register as Player
                </button>
              </div>
            )}
          </nav>

          <button
            className="xl:hidden shrink-0 text-slate-700 bg-white border border-emerald-200 rounded-xl p-2 shadow-sm"
            onClick={() => setMobileMenu((prev) => !prev)}
            aria-label={mobileMenu ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenu}
            aria-controls="mobile-nav-drawer"
          >
            {mobileMenu ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>

        {mobileMenu ? (
          <div className="xl:hidden fixed inset-0 z-[9999] bg-white">
            <div className="h-full flex flex-col">
              <div className="shrink-0 border-b border-emerald-200 bg-gradient-to-r from-emerald-100 to-teal-50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {!isLoggedIn ? (
                    <img
                      src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                      alt="Logo"
                      className="h-10 w-10 rounded-full object-cover border border-emerald-200"
                    />
                  ) : null}
                  <h2 className="text-2xl font-extrabold text-slate-800 truncate">Menu</h2>
                </div>
                <button
                  onClick={() => setMobileMenu(false)}
                  aria-label="Close menu"
                  className="rounded-lg p-2 hover:bg-emerald-200 transition-colors"
                >
                  <FaTimes size={22} />
                </button>
              </div>

              <nav id="mobile-nav-drawer" className="flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-20">
                <div className="flex flex-col gap-1">
                  {navItems && navItems.length > 0 ? (
                    navItems.map((item, idx) => (
                      <button
                        key={item.path || `${item.name || "menu"}-${idx}`}
                        onClick={() => {
                          setMobileMenu(false);
                          navigate(item.path);
                        }}
                        className="w-full text-left text-lg font-semibold py-2 px-3 rounded-xl border border-transparent hover:border-emerald-200 hover:text-emerald-900 hover:bg-emerald-50 transition-colors"
                      >
                        {item.name}
                      </button>
                    ))
                  ) : (
                    <div className="text-gray-500 italic text-sm py-2">No menu items available.</div>
                  )}
                </div>

                <div className="mt-5 border-t pt-5 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                  {token ? (
                    <div className="space-y-3">
                      <button onClick={() => { setMobileMenu(false); navigate(role === "player" ? "/player-profile" : "/admin/profile"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-emerald-900 hover:bg-emerald-50 transition-colors">Profile</button>
                      <button onClick={() => { setMobileMenu(false); navigate("/change-password"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-emerald-900 hover:bg-emerald-50 transition-colors">Change Password</button>
                      <button onClick={() => { setMobileMenu(false); handleLogout(); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors">Logout</button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          setMobileMenu(false);
                          navigate("/login");
                        }}
                        className="text-center text-lg font-semibold text-emerald-900 border-2 border-emerald-400 rounded-xl w-full py-3 hover:bg-emerald-100 transition-all"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenu(false);
                          navigate("/register");
                        }}
                        className="text-center text-lg font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl w-full py-3 hover:from-emerald-700 hover:to-teal-700 transition-all"
                      >
                        Register as Player
                      </button>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        ) : null}
      </header>
    );
  }

  return (
    <header className={HEADER_SHELL_CLASS}>
      <div className={`${HEADER_SURFACE_CLASS} border-t border-emerald-200/80`}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className={`grid grid-cols-1 ${isLoggedIn ? "" : "lg:grid-cols-[minmax(260px,360px)_minmax(0,1fr)]"} gap-5 lg:gap-6 items-center`}>
            <button
              className="flex items-center justify-center lg:justify-start gap-3 text-left"
              onClick={() => navigate("/")}
            >
              {!isLoggedIn ? (
                <img
                  src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                  alt="Para Sports Association of Andhra Pradesh"
                  className="h-16 sm:h-20 lg:h-24 w-auto object-contain shrink-0"
                />
              ) : null}
              {!isLoggedIn ? (
                <div className="min-w-2">
                  {/* <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.2em] text-amber-700">
                    Para Sports Association of Andhra Pradesh
                  </p> */}
                  <p className="text-base sm:text-xl lg:text-[1.5rem] font-black leading-none text-slate-900">
                    Para Sports Association of Andhra Pradesh
                  </p>
                  <p className="mt-1.5 text-[9px] sm:text-xs lg:text-[10px] font-semibold text-slate-900">
                    Affiliated to Paralympic Committee of India
                  </p>
                  <p className="mt-1 text-[9px] sm:text-xs lg:text-[10px] font-semibold text-slate-900">
                    Recognized by Sports Authority of Andhra Pradesh
                  </p>
                </div>
              ) : null}
            </button>

            {!isLoggedIn ? (
              <div className="min-w-0 flex flex-col sm:flex-row items-center justify-center lg:justify-end gap-4 lg:gap-5">
                <div className="min-w-0 text-center sm:text-left lg:text-right">
                  <h1 className="text-[1.35rem] sm:text-[1.7rem] lg:text-[2.35rem] leading-none font-black tracking-tight text-[#5a1c3c]">
                    {headerName}
                  </h1>
                  <div className="mt-2 space-y-1">
                    {resolvedHeaderLines.map((line, index) => (
                      <p
                        key={`${line}-${index}`}
                        className="text-sm sm:text-lg lg:text-[1.25rem] font-extrabold tracking-tight text-[#1d3c73]"
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="h-24 w-24 sm:h-28 sm:w-28 lg:h-32 lg:w-32 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
                  <img
                    src={headerPhotoUrl}
                    alt={headerName}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_HEADER_PROFILE.imageSrc;
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className={`${HEADER_SURFACE_CLASS} border-t border-emerald-300`}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden xl:flex items-center gap-5 py-3">
            <nav className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max items-center gap-2 pr-2">
                {navItems.map((item, idx) => (
                  <button
                    key={item.path || `${item.name || "menu"}-${idx}`}
                    onClick={() => navigate(item.path)}
                    className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-full border text-sm font-bold transition-all ${
                      isItemActive(item.path)
                        ? "border-emerald-700 bg-emerald-700 text-white shadow-sm"
                        : "border-emerald-200 bg-white/95 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-900"
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </nav>

            {token ? (
              <div className="relative shrink-0" ref={dropdownRef}>
                <div
                  onClick={() => setShowDropdown((s) => !s)}
                  className="flex items-center gap-3 cursor-pointer rounded-full border border-emerald-200 bg-white/90 pl-2 pr-3 py-1.5 hover:bg-emerald-100 transition-colors"
                >
                  <img
                    src={photoUrl}
                    alt="User"
                    className="w-10 h-10 rounded-full object-cover border-2 border-emerald-200"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/placeholder-user1.png";
                    }}
                  />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-slate-900">{displayName}</div>
                    <div className="text-xs text-slate-500 capitalize">{role}</div>
                  </div>
                </div>

                {showDropdown && (
                  <div className="absolute right-0 mt-3 bg-white text-black rounded-2xl shadow-xl w-56 p-2 border border-emerald-200">
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        if (role === "player") navigate("/player-profile");
                        else if (["admin", "secretary", "district"].includes(role)) navigate("/admin/profile");
                        else navigate("/player-profile");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/change-password");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-rose-50 text-rose-600 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => navigate("/login")}
                  className="font-semibold text-emerald-900 border border-emerald-300 bg-emerald-100 rounded-full px-4 py-2 hover:bg-emerald-200 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full px-5 py-2 hover:from-emerald-700 hover:to-teal-700 shadow-sm transition-all duration-300"
                >
                  Register as Player
                </button>
              </div>
            )}
          </div>

          <div className="xl:hidden flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Navigation</p>
              {!isLoggedIn ? (
                <p className="text-sm font-semibold text-slate-900 truncate">
                  Para Sports Association of Andhra Pradesh
                </p>
              ) : null}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {!token ? (
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-emerald-900 border border-emerald-300 bg-emerald-100 rounded-full px-3 py-1.5 hover:bg-emerald-200 transition-colors"
                >
                  Login
                </button>
              ) : null}
              <button
                className="text-slate-700 bg-white border border-emerald-200 rounded-xl p-2 shadow-sm"
                onClick={() => setMobileMenu((prev) => !prev)}
                aria-label={mobileMenu ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenu}
                aria-controls="mobile-nav-drawer"
              >
                {mobileMenu ? <FaTimes size={22} /> : <FaBars size={22} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenu ? (
        <div className="xl:hidden fixed inset-0 z-[9999] bg-white">
          <div className="h-full flex flex-col">
            <div className={`${HEADER_SURFACE_CLASS} shrink-0 border-b border-emerald-200 px-4 py-3 flex items-center justify-between`}>
              <div className="flex items-center gap-3 min-w-0">
                {!isLoggedIn ? (
                  <img
                    src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                    alt="Logo"
                    className="h-10 w-10 rounded-full object-cover border border-emerald-200"
                  />
                ) : null}
                <div className="min-w-0">
                  <h2 className="text-xl font-extrabold text-slate-900 truncate">
                    {isLoggedIn ? "Menu" : headerName}
                  </h2>
                  <p className="text-xs text-slate-700 truncate">
                    {isLoggedIn ? "Navigation" : resolvedHeaderLines[0] || "Site menu"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenu(false)}
                aria-label="Close menu"
                className="rounded-lg p-2 hover:bg-emerald-100 transition-colors"
              >
                <FaTimes size={22} />
              </button>
            </div>

            <nav id="mobile-nav-drawer" className="flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-20">
              {!isLoggedIn ? (
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-5">
                  <img
                    src={headerPhotoUrl}
                    alt={headerName}
                    className="h-20 w-20 rounded-2xl object-cover border border-slate-200 bg-white"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_HEADER_PROFILE.imageSrc;
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-lg font-extrabold text-[#5a1c3c] truncate">{headerName}</div>
                    {resolvedHeaderLines.map((line, index) => (
                      <div key={`${line}-${index}`} className="text-sm font-semibold text-[#1d3c73] truncate">
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col gap-1">
                {navItems && navItems.length > 0 ? (
                  navItems.map((item, idx) => (
                    <button
                      key={item.path || `${item.name || "menu"}-${idx}`}
                      onClick={() => {
                        setMobileMenu(false);
                        navigate(item.path);
                      }}
                      className="w-full text-left text-lg font-semibold py-2 px-3 rounded-xl border border-transparent hover:border-teal-200 hover:text-teal-900 hover:bg-teal-50 transition-colors"
                    >
                      {item.name}
                    </button>
                  ))
                ) : (
                  <div className="text-gray-500 italic text-sm py-2">No menu items available.</div>
                )}
              </div>

              <div className="mt-5 border-t pt-5 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                {token ? (
                  <div className="space-y-3">
                    <button onClick={() => { setMobileMenu(false); navigate(role === "player" ? "/player-profile" : "/admin/profile"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-teal-900 hover:bg-teal-50 transition-colors">Profile</button>
                    <button onClick={() => { setMobileMenu(false); navigate("/change-password"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-teal-900 hover:bg-teal-50 transition-colors">Change Password</button>
                    <button onClick={() => { setMobileMenu(false); handleLogout(); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors">Logout</button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setMobileMenu(false);
                        navigate("/login");
                      }}
                      className="text-center text-lg font-semibold text-teal-900 border-2 border-teal-300 rounded-xl w-full py-3 hover:bg-teal-50 transition-all"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenu(false);
                        navigate("/register");
                      }}
                      className="text-center text-lg font-semibold text-white bg-gradient-to-r from-teal-700 to-cyan-700 rounded-xl w-full py-3 hover:from-teal-800 hover:to-cyan-800 transition-all"
                    >
                      Register  as Player
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;
